import React from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import './capability-graph-small.css';

export class CapabilityGraphSmall extends React.Component {

    constructor(props) {
        super(props);
        this.myRef = React.createRef();
        this.comId = (Math.random() * 1000000<<0).toString(16);
        this.timer = null;
        this.container = null;
        this.state = {
            lines: props.lines,
            axis: props.axis
        };
        this.width = props.size.width || 600;
        this.height = props.size.height || 400;
        this.refreshChart = this.refreshChart.bind(this);
    }

    componentDidMount() {
        this.container = this.myRef.current;
        this.width = this.myRef.current.clientWidth - 20;
        window.addEventListener('resize', this.refreshChart, false);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.refreshChart, false);
    }

    refreshChart = () => {
        this.timer = setTimeout(() => {
            if (this.timer) {
                clearTimeout(this.timer);
            }
            // Temp fix. Willunmount lifecycle is not called on route change
            // React Router has some issue.
            if (typeof this.myRef === 'undefined' || !this.myRef.current.clientWidth) {
                return;
            }
            this.width = this.myRef.current.clientWidth - 20;
            this.renderChart();
        }, 100);
        
    }

    componentWillReceiveProps(newProps) {
        var shouldUpdate = false;
        var obj = {};
        if (this.state.lines !== newProps.lines) {
            obj.lines = newProps.lines;
            shouldUpdate = true;
        }

        if (this.state.axis !== newProps.axis) {
            obj.axis = newProps.axis;
            shouldUpdate = true;
        }

        if (newProps.reload !== this.props.reload) {
            obj.reload = newProps.reload;
            shouldUpdate = true;
        }
        
        if (shouldUpdate) {
            this.setState(obj, function() {
                this.refreshChart();
            });
        }
        
    }

    lineFunc(xRange, yRange, d) {
        let axis = this.state.axis;
        return d3.line()
            .x(function(d) {
                return xRange(Math.max(Math.min(axis.x.max, d.x), axis.x.min));
            })
            .y(function(d) {
                return yRange(Math.max(Math.min(axis.y.max, d.y), axis.y.min));
            })
            .curve(d3.curveLinear)(d); 
    }


    renderDotsAndTexts(vis, loc, xRange, yRange, color, isTop, isTarget) {
        let axis = this.state.axis;
        let yPad = isTop ? -10 : 15;
        vis.append('svg:text')
            .attr('class', 'visualisation--line-text')
            .attr('text-anchor', 'middle')
            .attr('x', xRange(Math.max(Math.min(axis.x.max, loc.x), axis.x.min)))
            .attr('y', yRange(Math.max(Math.min(axis.y.max, loc.y), axis.y.min)) + yPad)
            .attr('fill', 'currentColor')
            .text(loc.x + ', ' + loc.y);
            
        vis.append('svg:circle')
            .attr('class', 'visualisation--line-dot')
            .attr('cx',  xRange(Math.max(Math.min(axis.x.max, loc.x), axis.x.min)))
            .attr('cy', yRange(Math.max(Math.min(axis.y.max, loc.y), axis.y.min)))
            .attr('fill', color)
            .attr('stroke', isTarget ? '#fff' : color)
            .attr('r', isTarget? 6 : 4);
    }

    renderLine(vis, item, xRange, yRange) {
        vis.append('svg:path')
            .attr('d', this.lineFunc(xRange, yRange, item.lineData))
            .attr('class', 'visualisation--line ')
            .attr('stroke', item.color)
            .attr('stroke-width', 4)

            .attr('fill', 'none');
    }

    renderChart() {
        var svg = d3.select('.visualisation-line-'+this.comId);
        var margin = { top: 20, right: 20, bottom: 30, left: 40 };
        var lines = this.state.lines;
        var axis = this.state.axis;

        var width, height;
        width = this.width - margin.left - margin.right;
        height = this.height - margin.top - margin.bottom;

        if (!lines) {
            return;
        }


        svg.attr("width", this.width)
            .attr("height", this.height);

        svg.selectAll('*').remove();

        var x = d3.scaleLinear()
            .domain([axis.x.min, axis.x.max])
            .range([0, width])
            .nice(5);

        var y = d3.scaleLinear()
            .domain([axis.y.min, axis.y.max])
            .range([height, 0])
            .nice(5);


        //creates gridlines
        var g = svg.append("g")
            .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
            .attr("class", "grid")
            .call(
                d3.axisRight(y)
                .ticks(5)
                .tickSize(width)
                .tickFormat("")
            )  

        g.append("g")
            .attr("transform", "translate(0," + height + ")")  
            .attr("class", "grid")  
            .call(d3.axisBottom(x)
                .ticks(5)
                .tickSize(-height)
                .tickFormat("")
            )  
       
        //creates axis labels and scale values
        g.append("g")
            .attr("transform", "translate(0," + height + ")")
            .call(
                d3.axisBottom(x)
                    .ticks(5)
            )
            .append("text")
            .attr("fill", "#000")
            .attr("y", -7)
            .attr("x", width)
            .attr("text-anchor", "end")
            .text(axis.x.name);
        
        g.append("g")
            .call(
                d3.axisLeft(y)
                    .ticks(5)
            )
            .append("text")
            .attr("fill", "#000")
            .attr("transform", "rotate(-90)")
            .attr("y", 6)
            .attr("dy", "0.71em")
            .attr("text-anchor", "end")
            .text(axis.y.name);

            this.state.lines.map((item) => {
                const lineGroup = g.append('g')
                            .attr('title', item.title)
                            .attr('class','visualisation--line-group');
                        
                this.renderLine(lineGroup, item, x, y);
                item.lineData.map((loc, idx) => {
                    this.renderDotsAndTexts(lineGroup, loc, x, y, item.color, idx);
                    return true;
                });
                if (item.target) {
                    this.renderDotsAndTexts(lineGroup, item.target, x, y, '#4da00a', 100, true);
                }
                return true;
            });

    }

    render() {
        return <div className="vis" ref={this.myRef}>
                <svg 
                    className={'visualisation-line-'+this.comId} 
                    width="300"
                    height="400">
                </svg>
        </div>;
    }


}

CapabilityGraphSmall.propTypes = {
    size: PropTypes.object,
    lines: PropTypes.array.isRequired,
    reload: PropTypes.bool,
    axis: PropTypes.object.isRequired,
    isCarbon: PropTypes.bool,
    title: PropTypes.string,
    assignClass: PropTypes.string
};

CapabilityGraphSmall.defaultProps = {
    size: {width: 800, height: 400},
    axis: {x: {min: 0, max: 100, name: 'x-axis'}, y: {min: 0, max: 100, name: 'y-axis'}}
};


// WEBPACK FOOTER //
// ./src/components/capability-graph-small/index.js